import { Link } from "react-router-dom";
import { Star, ShoppingCart } from "lucide-react";
import { motion } from "framer-motion";
import { useCurrency } from "@/context/CurrencyContext";
import { useCart } from "@/context/CartContext";
import { ComingSoonOverlay } from "@/components/ComingSoonBadge";
import { Button } from "@/components/ui/button";

const INV = {
  in_stock: { label: "In Stock", cls: "text-[#00E676]" },
  low_stock: { label: "Low Stock", cls: "text-[#FFB800]" },
  out_of_stock: { label: "Sold Out", cls: "text-[#FF2A6D]" },
};

export function ProductCard({ product, index = 0 }) {
  const { format } = useCurrency();
  const { addItem } = useCart();
  const inv = INV[product.inventory_status] || INV.in_stock;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.06 }}
      className="dw-card group relative flex flex-col overflow-hidden rounded-2xl"
      data-testid={`product-card-${product.product_id}`}
    >
      <Link to={`/product/${product.product_id}`} className="relative block aspect-square overflow-hidden bg-[#0B0D16]">
        <img src={product.images?.[0]} alt={product.name} loading="lazy"
          className="h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-110" />
        {product.coming_soon && <ComingSoonOverlay />}
      </Link>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="mb-2 flex items-center justify-between">
          <span className={`font-mono text-[10px] font-bold uppercase tracking-wider ${inv.cls}`}>{inv.label}</span>
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Star className="h-3 w-3 fill-[#FFB800] text-[#FFB800]" /> {product.rating}
          </span>
        </div>
        <Link to={`/product/${product.product_id}`}>
          <h3 className="font-display text-[15px] font-semibold leading-snug text-slate-100 transition-colors group-hover:text-[#00F0FF] line-clamp-2">
            {product.name}
          </h3>
        </Link>
        <div className="mt-3 flex items-end justify-between">
          <div>
            <span className="font-mono text-lg font-bold text-white">{format(product.price_usd)}</span>
            {product.compare_at_usd && (
              <span className="ml-2 font-mono text-xs text-slate-500 line-through">{format(product.compare_at_usd)}</span>
            )}
          </div>
        </div>
        <Button
          onClick={() => addItem(product)}
          data-testid={`add-to-cart-${product.product_id}`}
          className="mt-4 w-full rounded-xl bg-[#00F0FF] font-semibold text-[#08090E] hover:bg-[#33F3FF] transition-colors"
        >
          <ShoppingCart className="mr-2 h-4 w-4" /> Add to Cart
        </Button>
      </div>
    </motion.div>
  );
}
